import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { mapActions } from "pinia";

import { IOpenOrder } from "../../../resources-vue/vue-interfaces/i-plan-of-care";
import { ITypeaheadOptions } from "../../../resources-vue/vue-interfaces/i-typeahead";
import { IGetUserSlimInfoResult } from "../../../resources-vue/vue-interfaces/i-user";
import type { INavigateRequestParams, INavigateResponse } from "../../../resources-vue/vue-interfaces/i-navigation";
import { PhysiciansService } from "../../../services/physician-service";
import { TaskService } from "../../../services/task.service";
import { ToastrService } from "../../../services/toastr.service";
import { CurrentUserService } from "../../../services/current-user-service";
import { NavigationService } from "../../../services/navigation.service";
import { formatName } from "../../../common/format-name";
import { formatDate, formatDateInput } from "../../../common/vue-helpers/modifiers/value-modifier";
import { useLoadingIndicator } from "../../../pages-vue/Common/LoadingIndicator/store/useLoadingIndicator";

import FormatName from "../FormatName/FormatName.vue";
import FormSection from "../FormSection/FormSection.vue";
import DatePicker from "../DatePicker/DatePicker.vue";
import TypeaheadInput from "../TypeaheadInput/TypeaheadInput.vue";
import PhysicianOrderCard from "./PhysicianOrderCard.vue";

export default defineComponent({
    components: {
        FormatName,
        FormSection,
        DatePicker,
        TypeaheadInput,
        PhysicianOrderCard
    },
    props: {
        orderId: {
            type: String,
            default: null
        },
        urlNoteId: {
            type: String,
            default: null
        },
        isAttachOrderNotRequired: {
            type: Boolean,
            default: false
        },
        closeAttachOrder: { type: Function, default: null },
        openOrders: { type: Array as PropType<IOpenOrder[]>, default: [] },
        entity: {
            type: String,
            default: null
        },
        application: {
            type: Number,
            default: null
        },
        accountId: {
            type: String,
            default: null
        },
        providerId: {
            type: String,
            default: null
        },
        patientId: {
            type: String,
            default: null
        },
        referralId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            logCategory: "Open-Order",
            isProviderOrderRequested: false,
            isNewOrderRequested: false,
            isNewOrderInProgress: false,
            isChooseOrderRequested: true,
            orderDate: moment().format("MM/DD/YYYY"),
            orderSelectedPhysician: null as ITypeaheadOptions,
            currentUser: null as IGetUserSlimInfoResult,
            _physicianService: null,
            _taskService: null,
            _toastrService: null,
            _currentUserService: null,
            _navigationService: null,
            formatDate,
            formatDateInput,
            v$: useVuelidate()
        };
    },
    validations() {
        return {
            orderDate: { required },
            orderSelectedPhysician: { required }
        };
    },
    computed: {
        isUrlOrderSelected() {
            return !!this.orderId && !!this.urlNoteId && this.orderId === this.urlNoteId;
        },
        currentOrder() {
            return this.openOrders.find((order) => order.OrderId == this.orderId);
        }
    },
    watch: {
        orderId() {
            this.isChooseOrderRequested = !this.orderId;
        }
    },
    emits: ["update:isAttachOrderNotRequired", "update:orderId"],
    created() {
        this._physicianService = Container.instance.get(PhysiciansService);
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._currentUserService = Container.instance.get(CurrentUserService);
        this._navigationService = Container.instance.get(NavigationService);
    },
    async mounted() {
        this.currentUser = await this._currentUserService.getUser();
        this.$emit("update:isAttachOrderNotRequired", false);
    },
    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        requestProviderOrder() {
            this.isProviderOrderRequested = true;
        },
        closeRequestProviderOrder() {
            this.isProviderOrderRequested = false;
            this.$emit("update:isAttachOrderNotRequired", true);
            this.closeAttachOrder();
        },
        openNewOrder() {
            this.isNewOrderRequested = true;
            this.orderDate = moment().format("MM/DD/YYYY");
            this.orderSelectedPhysician = null;
        },
        async getPhysicians(filter: string, limit: number) {
            try {
                // Test this one, it already exists
                let physicians = await this._physicianService.getPhysiciansByName({
                    name: filter,
                    page: 1,
                    pageLength: limit,
                    providerIds: [this.providerId],
                    application: this.application,
                    accountId: this.accountId
                });
                return physicians.items?.map((physician: any) => ({
                    name: this.getPhysicianName(physician.name),
                    value: physician.id
                }));
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        getPhysicianName(fullName: string) {
            let names = fullName.split(" ");
            return formatName(names[0], names[1]);
        },
        selectOrder(order: IOpenOrder) {
            this.isProviderOrderRequested = true;
            this.$emit("update:orderId", order.OrderId);
        },
        async openOrder(order: IOpenOrder) {
            const analyticsOptions = {
                category: this.logCategory,
                action: "Open-Order"
            };
            const navParams: INavigateRequestParams = {
                agencyId: this.accountId,
                userId: this.referralId,
                applicationId: this.application,
                taskId: this.orderId,
                patientId: this.patientId
            };

            try {
                this.showLoading(true);
                let response: INavigateResponse = await this._navigationService.navigateToTask(navParams);
                this._navigationService.redirect(response, analyticsOptions);
            } catch (e) {
                console.error(e);
                this.showLoading(false);
            }
        },
        closeOrderSelection() {
            this.isProviderOrderRequested = false;
            if (this.orderId) {
                this.isChooseOrderRequested = false;
            } else {
                this.closeAttachOrder();
            }
        },
        changeOrder() {
            this.isChooseOrderRequested = true;
        },
        handleExit() {
            if (this.urlNoteId) {
                this.$router.push(`/note/${this.urlNoteId}`);
            } else {
                this.closeAttachOrder();
            }
        },
        onDateChange(date: any, name: string) {
            if (name === "order-date") this.orderDate = date;
        },
        orderSelectedPhysicianChanged(date: ITypeaheadOptions) {
            this.orderSelectedPhysician = date;
        },
        async createPhysicianOrder() {
            const validated = await this.v$.$validate();
            if (validated) {
                try {
                    this.isNewOrderInProgress = true;
                    let createOrderResponse;
                    let physicianNameSplit = this.orderSelectedPhysician.name.split(", ");
                    let physicianId = this.orderSelectedPhysician.value;
                    let physicianFirstName = physicianNameSplit[1];
                    let physicianLastName = physicianNameSplit[0];
                    let query = {
                        agencyId: this.accountId,
                        application: this.application,
                        patientId: this.patientId,
                        scheduleDate: this.orderDate,
                        physicianId: this.orderSelectedPhysician.value,
                        physicianFirstName,
                        physicianLastName
                    };
                    // Test this endpoint
                    createOrderResponse = await this._taskService.createPalliativeCareOrder(query);
                    this._toastrService.success({
                        title: `Palliative Care Provider Order Created and Selected`,
                        message: `Updates on ${this.entity} for the patient would be available on the new Order created.`
                    });
                    this.openOrders.push({
                        PhysicianFirstName: physicianFirstName,
                        PhysicianLastName: physicianLastName,
                        PhysicianId: physicianId,
                        OrderId: createOrderResponse.id,
                        OrderDate: this.orderDate,
                        OrderStatus: "Saved"
                    });
                    this.$emit("update:orderId", createOrderResponse.Id);
                    this.isChooseOrderRequested = false;
                    this.closeNewOrder();
                } catch (e) {
                    console.error(e);
                    this._toastrService.error({
                        title: `Error`,
                        message: `There was a problem while creating a Provider Order. Please try again.`
                    });
                } finally {
                    this.isNewOrderInProgress = false;
                }
            }
        },
        closeNewOrder() {
            this.isProviderOrderRequested = true;
            this.isNewOrderRequested = false;
        }
    }
});
